// components/portal/ChatSearchModal.tsx
'use client';

import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { SquarePen, X } from '@/components/ui/lucide-memo';
import type { ChatSession, MemberData } from '#types/types';
import { useKernelStore } from '@/kernel/kernelStores';
import { getActionPanelTitle } from '#lib/leasing/actionPanelTitles';
import {
  getSessionTableFocusLabel,
  isTableFocusSubject,
  resolvePortalViewFromTableFocus,
} from '#lib/leasing/session-focus';
import { getUnitDisplayName } from './entityDisplay';
import { useKernel } from '@/kernel/Kernel';
import { DesignerIcon } from '@/components/ui/CustomIcons';
import {
  InventoryList,
  type InventoryListRenderState,
} from './InventoryList';
import { UserSelector } from './UserSelector';
import { readCampaignRow } from '#lib/leasing/campaignRow';

type ChatSearchModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onOpenSession: (sessionId: string) => void;
  onNewChat: () => void;
};

type FocusedProjectPreview = {
  imageUrl: string | null;
  name: string;
};

const CHAT_SEARCH_SORT_OPTIONS = [
  {
    label: 'Recently updated',
    field: 'updatedAt',
    localAccessor: (session: ChatSession) => session.updatedAt || session.createdAt,
  },
];

const CHAT_GROUP_SECTION_ORDER = ['Today', 'Yesterday', 'Previous 7 Days', 'Older'] as const;

// Creation-scope module chips ("agents live in projects"): let the global Chats
// view filter to where conversations were born. 'All' is the default, scoped
// history is never hidden, only groupable.
const CHAT_MODULE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'global', label: 'Global' },
  { key: 'studio', label: 'Studio' },
  { key: 'source', label: 'Source' },
  { key: 'analyst', label: 'Analyst' },
] as const;

type ChatModuleFilter = (typeof CHAT_MODULE_FILTERS)[number]['key'];

const MODAL_FONT_STYLE: CSSProperties = {
  fontFamily: 'ui-sans-serif, -apple-system, system-ui, sans-serif',
};

const SEARCH_INPUT_STYLE: CSSProperties = {
  fontFamily: 'ui-sans-serif, -apple-system, system-ui, sans-serif',
  lineHeight: '20px',
  fontSize: 16,
  // globals.css outlines every focused input; ChatGPT's search field shows no box.
  outline: 'none',
  boxShadow: 'none',
  border: 0,
};

const buildFloorFocusLabel = (
  floor: { name?: string | null; level?: number | null } | null | undefined,
  lang: 'en' | 'fr'
): string => {
  const floorLabel = lang === 'fr' ? 'Etage' : 'Floor';
  const levelLabel = lang === 'fr' ? 'Niveau' : 'Level';
  const name = typeof floor?.name === 'string' ? floor.name.trim() : '';
  const hasLevel = typeof floor?.level === 'number' && Number.isFinite(floor.level);

  if (hasLevel && name) {
    const normalizedName = name.toLowerCase();
    const normalizedFloorLabel = floorLabel.toLowerCase();
    const normalizedLevelLabel = levelLabel.toLowerCase();
    const normalizedLevel = String(floor!.level);
    if (
      normalizedName === `${normalizedFloorLabel} ${normalizedLevel}` ||
      normalizedName === `${normalizedLevelLabel} ${normalizedLevel}`
    ) {
      return `${floorLabel} ${floor!.level}`;
    }
    return `${floorLabel} ${floor!.level} - ${name}`;
  }

  if (hasLevel) return `${floorLabel} ${floor!.level}`;
  if (name) return `${floorLabel} ${name}`;
  return floorLabel;
};

const formatActionTypeLabel = (value: string): string =>
  value
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');

const formatEntityTypeLabel = (value: string): string =>
  value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');

const getFirstUserMessage = (
  session: ChatSession,
  getMessages: (sessionId: string) => any[]
): string | null => {
  const firstUserMessage = getMessages(session.id).find(
    (message) => message.role === 'user' && typeof message.content === 'string' && message.content.trim().length > 0
  );
  return firstUserMessage?.content?.trim() || null;
};

const groupChatSessionsByDate = (sessions: ChatSession[]) => {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterdayStart = todayStart - 24 * 60 * 60 * 1000;
  const weekStart = todayStart - 7 * 24 * 60 * 60 * 1000;

  const buckets: Record<(typeof CHAT_GROUP_SECTION_ORDER)[number], ChatSession[]> = {
    Today: [],
    Yesterday: [],
    'Previous 7 Days': [],
    Older: [],
  };

  sessions.forEach((session) => {
    const time = new Date(session.updatedAt || session.createdAt).getTime();
    if (time >= todayStart) buckets.Today.push(session);
    else if (time >= yesterdayStart) buckets.Yesterday.push(session);
    else if (time >= weekStart) buckets['Previous 7 Days'].push(session);
    else buckets.Older.push(session);
  });

  return buckets;
};

const renderHighlightedText = (text: string, query: string) => {
  const source = typeof text === 'string' ? text : '';
  const needle = query.trim();
  if (!needle) return source;

  const sourceLower = source.toLowerCase();
  const needleLower = needle.toLowerCase();
  if (!sourceLower.includes(needleLower)) return source;

  const nodes: ReactNode[] = [];
  let cursor = 0;
  let key = 0;
  while (cursor < source.length) {
    const matchIndex = sourceLower.indexOf(needleLower, cursor);
    if (matchIndex < 0) {
      const tail = source.slice(cursor);
      if (tail.length > 0) {
        nodes.push(<span key={`tail-${key}`}>{tail}</span>);
      }
      break;
    }

    const before = source.slice(cursor, matchIndex);
    if (before.length > 0) {
      nodes.push(<span key={`before-${key}`}>{before}</span>);
      key += 1;
    }

    const match = source.slice(matchIndex, matchIndex + needle.length);
    nodes.push(
      <mark key={`mark-${key}`} className="rounded bg-[#3B82F6]/25 px-0.5 text-white">
        {match}
      </mark>
    );
    key += 1;
    cursor = matchIndex + needle.length;
  }

  return <>{nodes}</>;
};

const resolveMessageMatchSnippet = (
  session: ChatSession,
  query: string,
  getMessages: (sessionId: string) => any[]
): string | null => {
  const needle = query.trim().toLowerCase();
  if (!needle) return null;

  const candidates = [
    session.lastMessageSnippet?.trim() || '',
    getFirstUserMessage(session, getMessages) || '',
  ].filter((value) => value.length > 0);

  const matched = candidates.find((candidate) => candidate.toLowerCase().includes(needle));
  if (!matched) return null;

  const lower = matched.toLowerCase();
  const matchIndex = lower.indexOf(needle);
  if (matchIndex < 0) return matched;

  const radius = 48;
  const start = Math.max(0, matchIndex - radius);
  const end = Math.min(matched.length, matchIndex + needle.length + radius);
  const prefix = start > 0 ? '…' : '';
  const suffix = end < matched.length ? '…' : '';
  return `${prefix}${matched.slice(start, end)}${suffix}`;
};

type ChatModuleFilterChipProps = {
  filterKey: ChatModuleFilter;
  label: string;
  isActive: boolean;
  onSelect: (filterKey: ChatModuleFilter) => void;
};

const ChatModuleFilterChip = memo(({ filterKey, label, isActive, onSelect }: ChatModuleFilterChipProps) => {
  const handleClick = useCallback(() => {
    onSelect(filterKey);
  }, [onSelect, filterKey]);

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-pressed={isActive}
      className={`rounded-full px-3 py-1 text-[12px] leading-[14px] transition-colors ${
        isActive
          ? 'bg-[#2F2F2F] text-white'
          : 'text-[#B5B5B5] hover:bg-[#262626] hover:text-[#E8E8E8]'
      }`}
      style={MODAL_FONT_STYLE}
    >
      {label}
    </button>
  );
});

ChatModuleFilterChip.displayName = 'ChatModuleFilterChip';

type ChatSearchSessionRowProps = {
  session: ChatSession;
  queryNeedle: string;
  onOpenSession: (sessionId: string) => void;
  buildActionSummary: (session: ChatSession) => { typeLabel: string; targetLabel: string } | null;
  buildFocusSummary: (session: ChatSession) => { typeLabel: string; targetLabel: string } | null;
  resolveFocusedProject: (session: ChatSession) => FocusedProjectPreview | null;
  resolveSessionEventCount: (session: ChatSession) => number;
  resolveSessionMessages: (sessionId: string) => any[];
};

const ChatSearchSessionRow = memo(({
  session,
  queryNeedle,
  onOpenSession,
  buildActionSummary,
  buildFocusSummary,
  resolveFocusedProject,
  resolveSessionEventCount,
  resolveSessionMessages,
}: ChatSearchSessionRowProps) => {
  const ownerId = typeof session.ownerId === 'string' ? session.ownerId.trim() : '';
  const ownerMember = useKernelStore((state) =>
    ownerId ? ((state.inventory.User.registry[ownerId] as MemberData | undefined) ?? null) : null
  );

  const handleOpen = useCallback(() => {
    onOpenSession(session.id);
  }, [onOpenSession, session.id]);

  const action = buildActionSummary(session);
  const fallbackMessage =
    getFirstUserMessage(session, resolveSessionMessages)
    || session.lastMessageSnippet?.trim()
    || session.name?.trim()
    || 'Untitled Session';

  const title = action
    ? `${action.typeLabel} - ${action.targetLabel}`
    : fallbackMessage;
  const messageMatchSnippet = resolveMessageMatchSnippet(session, queryNeedle, resolveSessionMessages);
  const eventCount = resolveSessionEventCount(session);
  const focused = buildFocusSummary(session);
  const focusedProject = resolveFocusedProject(session);

  return (
    <button
      type="button"
      onClick={handleOpen}
      className="flex w-full items-stretch rounded-[12px] py-0 pl-3 pr-0 text-left text-white transition-colors hover:bg-[#2A2A2A]"
      style={MODAL_FONT_STYLE}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3 py-3 pr-3">
        <div className="mt-0.5 shrink-0">
          {ownerMember ? (
            <UserSelector
              selectedMember={ownerMember}
              onClick={handleOpen}
            />
          ) : (
            <div className="flex h-7 w-7 items-center justify-center rounded-full border border-white/10 bg-[#232323] text-[11px] font-semibold leading-none text-white/70">
              U
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[14px] leading-[16px]">{renderHighlightedText(title, queryNeedle)}</p>
          {queryNeedle.length > 0 && messageMatchSnippet && (
            <p className="mt-1 truncate text-[11px] leading-[14px] text-white/65">
              {renderHighlightedText(messageMatchSnippet, queryNeedle)}
            </p>
          )}
          <div className="mt-1.5 flex items-center gap-2 text-[11px] leading-[16px] text-white/55">
            {focused && (
              <>
                <span className="block min-w-0 max-w-[70%] truncate leading-[16px]">
                  {focused.typeLabel
                    ? `${focused.typeLabel}: ${focused.targetLabel}`
                    : focused.targetLabel}
                </span>
                <span className="block leading-[16px] text-white/25">|</span>
              </>
            )}
            <span className="block leading-[16px]">{eventCount} events</span>
          </div>
        </div>
      </div>
      <div
        className={`w-[118px] shrink-0 self-stretch overflow-hidden${focusedProject ? ' rounded-r-[12px] bg-[#141414]' : ''}`}
        aria-hidden={!focusedProject}
      >
        {focusedProject ? (
          focusedProject.imageUrl ? (
            <img
              src={focusedProject.imageUrl}
              alt={focusedProject.name || 'Project preview'}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <DesignerIcon size={18} color="#3A3A3A" />
            </div>
          )
        ) : (
          <div className="h-full w-full" />
        )}
      </div>
    </button>
  );
});

ChatSearchSessionRow.displayName = 'ChatSearchSessionRow';

export const ChatSearchModal = memo(({
  isOpen,
  onClose,
  onOpenSession,
  onNewChat,
}: ChatSearchModalProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore(state => state.language);
  const currentUserId = useKernelStore((state) => state.userContext.dbUserId ?? '');
  const timelineFeedIndexBySessionId = useKernelStore((state) => state.timelineFeedIndex.bySessionId);
  const [query, setQuery] = useState('');
  const [moduleFilter, setModuleFilter] = useState<ChatModuleFilter>('all');
  const [mainAreaLeft, setMainAreaLeft] = useState(0);
  const [showExhaustedHint, setShowExhaustedHint] = useState(false);
  const inventorySearchChangeRef = useRef<((value: string) => void) | null>(null);
  const exhaustedHintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resolveSessionMessages = useCallback(
    (sessionId: string) => kernel.current?.getChatSessionMessages(sessionId) ?? [],
    [kernel]
  );

  const handleClose = useCallback(() => {
    setQuery('');
    setModuleFilter('all');
    setShowExhaustedHint(false);
    if (exhaustedHintTimerRef.current) {
      clearTimeout(exhaustedHintTimerRef.current);
      exhaustedHintTimerRef.current = null;
    }
    inventorySearchChangeRef.current?.('');
    onClose();
  }, [onClose]);

  const handleNewChat = useCallback(() => {
    setQuery('');
    setShowExhaustedHint(false);
    inventorySearchChangeRef.current?.('');
    onNewChat();
  }, [onNewChat]);

  const handleOpenSession = useCallback((sessionId: string) => {
    setQuery('');
    setShowExhaustedHint(false);
    inventorySearchChangeRef.current?.('');
    onOpenSession(sessionId);
  }, [onOpenSession]);

  const handleQueryChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    setQuery(nextValue);
    setShowExhaustedHint(false);
    inventorySearchChangeRef.current?.(nextValue);
  }, []);

  const stopPropagation = useCallback((event: ReactMouseEvent) => {
    event.stopPropagation();
  }, []);

  const handleSelectModuleFilter = useCallback((filterKey: ChatModuleFilter) => {
    setModuleFilter(filterKey);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (!isOpen) return;

    const updateMainAreaLeft = () => {
      const sidePanel = document.getElementById('portal-home-side-panel');
      const nextLeft = sidePanel?.getBoundingClientRect().right ?? 0;
      setMainAreaLeft(Math.max(0, Math.round(nextLeft)));
    };

    updateMainAreaLeft();

    const sidePanel = document.getElementById('portal-home-side-panel');
    let observer: ResizeObserver | null = null;
    if (sidePanel && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => updateMainAreaLeft());
      observer.observe(sidePanel);
    }

    window.addEventListener('resize', updateMainAreaLeft);
    return () => {
      window.removeEventListener('resize', updateMainAreaLeft);
      observer?.disconnect();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !currentUserId) return;
    void kernel.current?.ensureInventoryIds?.('User', [currentUserId]).catch(() => undefined);
  }, [currentUserId, isOpen, kernel]);

  useEffect(() => {
    if (isOpen) return;
    setShowExhaustedHint(false);
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (exhaustedHintTimerRef.current) {
        clearTimeout(exhaustedHintTimerRef.current);
      }
    };
  }, []);

  const flashExhaustedHint = useCallback(() => {
    if (query.trim().length === 0) return;
    setShowExhaustedHint(true);
    if (exhaustedHintTimerRef.current) {
      clearTimeout(exhaustedHintTimerRef.current);
    }
    exhaustedHintTimerRef.current = setTimeout(() => {
      setShowExhaustedHint(false);
      exhaustedHintTimerRef.current = null;
    }, 1100);
  }, [query]);

  const handleExhaustedScrollAttempt = useCallback((state: {
    hasMore: boolean;
    isLoading: boolean;
  }) => {
    if (state.hasMore || state.isLoading) return;
    flashExhaustedHint();
  }, [flashExhaustedHint]);

  const resolveActionTargetName = useCallback((session: ChatSession): string | null => {
    const activeAction = session.activeSubject;
    if (!activeAction?.entityType || !activeAction?.entityId) return null;
    if (isTableFocusSubject(activeAction)) {
      const tableView = resolvePortalViewFromTableFocus(activeAction);
      if (!tableView) return null;
      return getSessionTableFocusLabel(tableView, lang === 'fr' ? 'fr' : 'en');
    }

    const contextSummary = Array.isArray(session.contextSummary) ? session.contextSummary : [];
    const fromContextSummary = contextSummary.find(
      (anchor) => anchor.entityType === activeAction.entityType && anchor.entityId === activeAction.entityId
    )?.name?.trim();
    if (fromContextSummary) return fromContextSummary;

    const id = activeAction.entityId;
    // The switch has a `default` branch, so an unlisted member is handled, not dropped. Spelling out
    // every entity type would be a list nobody maintains and the default would still be the thing doing the work.
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (activeAction.entityType) {
      case 'Property':
        return kernel.current?.getInventory().Property.registry[id]?.name ?? null;
      case 'Floor': {
        const floor = kernel.current?.getInventory().Floor.registry[id];
        return buildFloorFocusLabel(floor, lang === 'fr' ? 'fr' : 'en');
      }
      case 'UnitPosition': {
        const inv = kernel.current?.getInventory();
        if (!inv) return lang === 'fr' ? 'Unite' : 'Unit';

        const unitFromContextSummary = contextSummary.find(
          (anchor) => anchor.entityType === 'Unit'
        )?.name?.trim();
        if (unitFromContextSummary) return unitFromContextSummary;

        const history = Array.isArray(session.subjectHistory) ? session.subjectHistory : [];
        for (let index = history.length - 1; index >= 0; index -= 1) {
          const entry = history[index];
          if (!entry || entry.entityType !== 'Unit' || typeof entry.entityId !== 'string') continue;
          const unitId = entry.entityId.trim();
          if (!unitId) continue;
          const unit = inv.Unit.registry[unitId];
          const label =
            getUnitDisplayName(unit, inv) ||
            unit?.name?.trim() ||
            (unit?.unitIdentifier?.trim() ? `Unit ${unit.unitIdentifier.trim()}` : null);
          if (label) return label;
        }

        return lang === 'fr' ? 'Unite' : 'Unit';
      }
      case 'Unit': {
        const inv = kernel.current?.getInventory();
        if(!inv) return null;
        const unit = inv.Unit.registry[id];
        return getUnitDisplayName(unit, inv);
      }
      case 'Portfolio':
        return kernel.current?.getInventory().Portfolio.registry[id]?.name ?? null;
      case 'Brand':
        return kernel.current?.getInventory().Brand.registry[id]?.name ?? null;
      case 'BrandLocation': {
        const inv = kernel.current?.getInventory();
        if (!inv) return lang === 'fr' ? 'Lieu marque' : 'Brand location';
        const locationFromRegistry = inv.BrandLocation.registry[id];
        const brandWithLocation = Object.values(inv.Brand.registry).find((brand) =>
          Array.isArray(brand.locations) && brand.locations.some((loc) => loc?.id === id)
        );
        const locationFromBrand = brandWithLocation?.locations?.find((loc) => loc?.id === id);
        const brandName = brandWithLocation?.name?.trim() || '';
        const city =
          locationFromRegistry?.city?.trim() ||
          locationFromBrand?.city?.trim() ||
          '';
        if (brandName && city) return `${brandName} - ${city}`;
        if (brandName) return brandName;
        if (city) return city;
        return lang === 'fr' ? 'Lieu marque' : 'Brand location';
      }
      case 'LegalEntity':
        return kernel.current?.getInventory().LegalEntity.registry[id]?.legalName ?? null;
      case 'MarketSignal': {
        const signal = kernel.current?.getInventory().MarketSignal.registry[id];
        return signal?.propertyName ?? signal?.unitIdentifier ?? null;
      }
      case 'SourcingCampaign':
        return readCampaignRow(kernel.current?.getInventory(), id)?.id ?? null;
      case 'Deal': {
        const deal = kernel.current?.getInventory().Deal.registry[id];
        if (!deal) return null;
        const brand = kernel.current?.getInventory().Brand.registry[deal.brandId];
        return brand?.name || deal.tenantLegalName || null;
      }
      case 'ChatSession':
        return kernel.current?.getInventory().ChatSession.registry[id]?.name ?? null;
      case 'GeneratedDocument':
        return kernel.current?.getInventory().GeneratedDocument.registry[id]?.templateName ?? null;
      case 'Lead': {
        return 'Un lead';
      }
      case 'Project':
        return kernel.current?.getInventory().Project.registry[id]?.name ?? null;
      case 'Organisation':
        return kernel.current?.getInventory().Organisation.registry[id]?.name ?? null;
      case 'User': {
        const user = kernel.current?.getInventory().User.registry[id];
        const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
        return fullName || user?.email || null;
      }
      default:
        return null;
    }
  }, [kernel, lang]);

  const buildActionSummary = useCallback((session: ChatSession): { typeLabel: string; targetLabel: string } | null => {
    const activeAction = session.activeSubject;
    if (!activeAction?.focusActionType) return null;

    const typeLabel = getActionPanelTitle(activeAction.focusActionType, lang) || formatActionTypeLabel(activeAction.focusActionType);
    const targetLabel = resolveActionTargetName(session) || 'Target pending';

    return { typeLabel, targetLabel };
  }, [resolveActionTargetName, lang]);

  const buildFocusSummary = useCallback((session: ChatSession): { typeLabel: string; targetLabel: string } | null => {
    const activeSubject = session.activeSubject;
    if (!activeSubject?.entityType || !activeSubject?.entityId) return null;
    if (isTableFocusSubject(activeSubject)) {
      const tableView = resolvePortalViewFromTableFocus(activeSubject);
      if (!tableView) return null;
      return {
        typeLabel: '',
        targetLabel: getSessionTableFocusLabel(tableView, lang === 'fr' ? 'fr' : 'en'),
      };
    }

    const typeLabel = formatEntityTypeLabel(activeSubject.entityType);
    const targetLabel = resolveActionTargetName(session) || (activeSubject.entityType === 'Lead' ? 'Un lead' : (lang === 'fr' ? 'Element' : 'Item'));
    return { typeLabel, targetLabel };
  }, [resolveActionTargetName, lang]);

  const resolveFocusedProject = useCallback((session: ChatSession): FocusedProjectPreview | null => {
    const activeSubject = session.activeSubject;
    if (!activeSubject?.entityType || !activeSubject?.entityId) return null;
    if (activeSubject.entityType !== 'Project') return null;
    if (isTableFocusSubject(activeSubject)) return null;
    const projectId = typeof activeSubject.entityId === 'string' ? activeSubject.entityId.trim() : '';
    if (!projectId) return null;
    const project = kernel.current?.getInventory().Project.registry[projectId];
    if (project) {
      return {
        imageUrl: project.imageUrl || null,
        name: project.name?.trim() || (lang === 'fr' ? 'Projet sans titre' : 'Untitled Project'),
      };
    }
    const contextSummary = Array.isArray(session.contextSummary) ? session.contextSummary : [];
    const contextName = contextSummary.find(
      (anchor) => anchor.entityType === 'Project' && anchor.entityId === projectId
    )?.name?.trim();
    return {
      imageUrl: null,
      name: contextName || (lang === 'fr' ? 'Projet' : 'Project'),
    };
  }, [kernel, lang]);

  const resolveSessionEventCount = useCallback((session: ChatSession): number => {
    const indexedTimelineCount = timelineFeedIndexBySessionId[session.id]?.length ?? 0;
    if (indexedTimelineCount > 0) return indexedTimelineCount;

    const messageCount = Number.isFinite(session.messageCount)
      ? Math.max(0, Math.floor(session.messageCount))
      : 0;
    const focusShiftCount = Array.isArray(session.subjectHistory)
      ? session.subjectHistory.length
      : 0;

    return messageCount + focusShiftCount;
  }, [timelineFeedIndexBySessionId]);

  const overlayStyle = useMemo<CSSProperties>(() => ({ left: `${mainAreaLeft}px` }), [mainAreaLeft]);

  const lockedFilters = useMemo(() => ({
    userId: currentUserId || '__current_user_missing__',
    ...(moduleFilter !== 'all' ? { creationModule: moduleFilter } : {}),
  }), [currentUserId, moduleFilter]);

  const renderList = useCallback((state: InventoryListRenderState<'ChatSession'>) => {
    inventorySearchChangeRef.current = state.handleSearchChange;

    const sessions = (state.viewItems as ChatSession[])
      .filter((session) => resolveSessionEventCount(session) > 0)
      .sort((a, b) => new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime());

    const grouped = groupChatSessionsByDate(sessions);
    const queryNeedle = query.trim();

    return (
      <>
        <button
          type="button"
          onClick={handleNewChat}
          className="mb-4 flex h-11 w-full items-center gap-3 rounded-[12px] px-3 text-left text-white transition-colors hover:bg-[#2A2A2A]"
          style={MODAL_FONT_STYLE}
        >
          <SquarePen size={20} strokeWidth={1.9} />
          <span className="text-[14px] leading-[14px]">New chat</span>
        </button>

        {state.viewIsLoading && query.trim().length > 0 && sessions.length === 0 && (
          <div className="mb-3 rounded-[10px] border border-white/10 bg-white/[0.03] px-3 py-2 text-[11px] text-white/55">
            Searching chats...
          </div>
        )}

        {CHAT_GROUP_SECTION_ORDER.map((sectionLabel) => {
          const items = grouped[sectionLabel] || [];
          if (items.length === 0) return null;

          return (
            <div key={sectionLabel} className="mb-4">
              <p className="mb-2 text-[13px] text-white/45">{sectionLabel}</p>
              <div className="space-y-0.5">
                {items.map((session) => (
                  <ChatSearchSessionRow
                    key={session.id}
                    session={session}
                    queryNeedle={queryNeedle}
                    onOpenSession={handleOpenSession}
                    buildActionSummary={buildActionSummary}
                    buildFocusSummary={buildFocusSummary}
                    resolveFocusedProject={resolveFocusedProject}
                    resolveSessionEventCount={resolveSessionEventCount}
                    resolveSessionMessages={resolveSessionMessages}
                  />
                ))}
              </div>
            </div>
          );
        })}

        {state.viewIsLoading && sessions.length > 0 && (
          <div className="py-2 text-center text-[11px] text-white/45">Loading more chats...</div>
        )}

        {!state.viewIsLoading && showExhaustedHint && query.trim().length > 0 && sessions.length > 0 && (
          <div className="py-2 text-center text-[11px] text-white/55">
            {lang === 'fr' ? 'Vous avez atteint la fin des resultats.' : 'You have reached the end of the results.'}
          </div>
        )}

        {!state.viewIsLoading && sessions.length === 0 && (
          <div className="flex h-24 items-center justify-center rounded-[12px] border border-white/10 bg-white/[0.02] text-sm text-white/60">
            {query.trim().length > 0 ? `No chats found for "${query.trim()}".` : 'No chats found.'}
          </div>
        )}
      </>
    );
  }, [
    query,
    resolveSessionEventCount,
    resolveSessionMessages,
    buildActionSummary,
    buildFocusSummary,
    resolveFocusedProject,
    handleNewChat,
    handleOpenSession,
    showExhaustedHint,
    lang,
  ]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-y-0 right-0 z-[120] flex items-start justify-center bg-black/35 p-6 pt-12"
      style={overlayStyle}
      onClick={handleClose}
    >
      <div
        className="flex h-[min(62vh,560px)] w-[min(92vw,760px)] flex-col overflow-hidden rounded-[22px] border border-white/10 bg-[#202020] shadow-[0_20px_55px_rgba(0,0,0,0.42)]"
        onClick={stopPropagation}
      >
        <div className="flex items-center border-b border-white/10 px-5 py-3">
          <input
            autoFocus
            value={query}
            onChange={handleQueryChange}
            placeholder="Search chats"
            className="w-full bg-transparent text-[14px] text-white placeholder:text-white/60 focus:outline-none"
            style={SEARCH_INPUT_STYLE}
          />
          <button
            type="button"
            onClick={handleClose}
            className="ml-3 flex h-8 w-8 items-center justify-center rounded-md text-white/70 transition-colors hover:bg-white/10 hover:text-white"
            aria-label="Close search"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex items-center gap-1.5 border-b border-[#2A2A2A] px-5 py-2.5">
          {CHAT_MODULE_FILTERS.map((option) => (
            <ChatModuleFilterChip
              key={option.key}
              filterKey={option.key}
              label={option.label}
              isActive={moduleFilter === option.key}
              onSelect={handleSelectModuleFilter}
            />
          ))}
        </div>

        <InventoryList
          key={moduleFilter}
          entityType="ChatSession"
          pageSize={query.trim().length > 0 ? 12 : 80}
          sortOptions={CHAT_SEARCH_SORT_OPTIONS}
          lockedFilters={lockedFilters}
          disableManualRefresh={true}
          wrapperClassName="custom-scrollbar flex-1 overflow-y-auto"
          contentClassName="px-5 py-3"
          onExhaustedScrollAttempt={handleExhaustedScrollAttempt}
          renderList={renderList}
        />
      </div>
    </div>
  );
});

ChatSearchModal.displayName = 'ChatSearchModal';
